if (!customElements.get('recently-viewed-recommendations')) {
    class RecentlyViewedRecommendations extends HTMLElement {
      constructor() {
        super();

        this.baseUrl = theme.routes.search_url + '?view=recently-viewed&type=product&q=';
        this.container = this.querySelector('.products');
        this.currentHandle = this.dataset.productHandle;
        this.max = parseInt(this.dataset.productsToShow, 10) || 8;
        this.recentlyViewedLimit = parseInt(this.dataset.recentlyViewedLimit, 10) || 3;
        this.fallbackCollection = this.dataset.fallbackCollection || '';

        this.recentlyViewedIds = [];
        this.products = [];
        this.fallbackProducts = [];

        this.flkty = null;


        if (window.localStorage) {
          try {
            const recentIds = window.localStorage.getItem('recently-viewed');
            if (recentIds) {
              const parsedIds = JSON.parse(recentIds);
              if (Array.isArray(parsedIds)) {
                this.recentlyViewedIds = parsedIds;
              }
            }
          } catch (e) {
            console.error('Recently viewed products: unable to parse localStorage.', e);
          }
        }
      }

      connectedCallback() {
        this.buildProducts();
      }

      /**
       * Build final product handles:
       *
       * 1. Recently viewed
       * 2. Fallback collection
       */
      async buildProducts() {
        try {
          const recentHandles = this.getRecentHandles();

          const remaining = this.max - recentHandles.length;

          let fallbackHandles = [];

          if (remaining > 0 && this.fallbackCollection) {
            fallbackHandles = await this.getFallbackHandles(remaining, recentHandles);
          }

          this.products = [...recentHandles, ...fallbackHandles].slice(0, this.max);

          if (!this.products.length) {
            return;
          }

          await this.fetchProducts();
        } catch (error) {
          console.error('Recently viewed products:', error);
        }
      }

      /**
       * Get Recently Viewed products.
       */
      getRecentHandles() {
        const handles = [];

        this.recentlyViewedIds.forEach((handle) => {
          // Recently Viewed 自己最多只取设定数量
          if (handles.length >= this.recentlyViewedLimit) {
            return;
          }

          if (!handle) {
            return;
          }

          // 排除当前产品
          if (handle === this.currentHandle) {
            return;
          }

          // 防止重复
          if (handles.includes(handle)) {
            return;
          }

          handles.push(handle);
        });

        return handles;
      }

      /**
       * Get fallback products from selected collection.
       *
       * We use Shopify's collection JSON endpoint here instead of
       * rendering all fallback products into the initial HTML.
       */
      async getFallbackHandles(requiredCount, recentHandles) {
        if (!this.fallbackCollection || requiredCount <= 0) {
          return [];
        }

        const url = `/collections/${encodeURIComponent(this.fallbackCollection)}/products.json?limit=50`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Unable to load fallback collection: ${response.status}`);
        }

        const data = await response.json();
        if (!data.products || !Array.isArray(data.products)) {
          return [];
        }

        const result = [];
        const excludedHandles = new Set([this.currentHandle, ...recentHandles]);

        data.products.forEach((product) => {
          if (result.length >= requiredCount) {
            return;
          }
          const handle = product.handle;
          if (!handle) {
            return;
          }
          if (excludedHandles.has(handle)) {
            return;
          }
          if (result.includes(handle)) {
            return;
          }
          result.push(handle);
        });

        return result;
      }

      /**
       * Fetch product cards through the existing
       * recently-viewed search template.
       */
      async fetchProducts() {
        const query = this.products.map((handle) => `handle:${handle}`).join(' OR ');

        const url = this.baseUrl + encodeURIComponent(query);

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Unable to load products: ${response.status}`);
        }

        const text = await response.text();

        const html = document.createElement('div');

        html.innerHTML = text;

        const recommendations = html.querySelector('.products');

        if (!recommendations || !recommendations.innerHTML.trim()) {
          return;
        }

        /**
         * Shopify search may not return products in exactly
         * the same order as our query.
         *
         * Reorder them according to:
         *
         * Recently Viewed
         * ->
         * Fallback Collection
         */
        const cards = Array.from(recommendations.children);

        const orderedCards = this.sortCardsByHandle(cards, this.products);

        this.container.innerHTML = '';

        orderedCards.forEach((card) => {
          this.container.appendChild(card);
        });

        this.classList.add('product-recommendations--loaded');

        this.initCarousel();

        this.initAnimations();
      }

      /**
       * Try to get product handle from product card.
       *
       * Different themes may store it in different places,
       * so several common attributes are supported.
       */
      getCardHandle(card) {
        if (!card) {
          return null;
        }

        // data-product-handle on the card itself
        if (card.dataset.productHandle) {
          return card.dataset.productHandle;
        }

        // data-handle on the card itself
        if (card.dataset.handle) {
          return card.dataset.handle;
        }

        // Search descendants
        const element = card.querySelector('[data-product-handle], [data-handle]');

        if (element) {
          return element.dataset.productHandle || element.dataset.handle || null;
        }

        // Last fallback: get handle from product URL.
        const link = card.querySelector('a[href*="/products/"]');

        if (link) {
          try {
            const url = new URL(link.href, window.location.origin);

            const match = url.pathname.match(/\/products\/([^/?#]+)/);

            if (match) {
              return match[1];
            }
          } catch (e) {
            return null;
          }
        }

        return null;
      }

      /**
       * Reorder fetched cards according to this.products.
       */
      sortCardsByHandle(cards, handles) {
        const cardMap = new Map();

        cards.forEach((card) => {
          const handle = this.getCardHandle(card);

          if (handle) {
            cardMap.set(handle, card);
          }
        });

        const ordered = [];

        handles.forEach((handle) => {
          const card = cardMap.get(handle);

          if (card) {
            ordered.push(card);
          }
        });

        /**
         * If some cards couldn't be identified by handle,
         * append them afterward rather than losing them.
         */
        cards.forEach((card) => {
          if (!ordered.includes(card)) {
            ordered.push(card);
          }
        });

        return ordered.slice(0, this.max);
      }

      /**
       * Initialize Flickity.
       */
      initCarousel() {
        if (!this.container) {
          return;
        }
        const cells = this.container.querySelectorAll('li.columns');

        if (!cells.length) {
          return;
        }

        // Destroy previous instance if necessary.
        if (this.flkty) {
          this.flkty.destroy();
          this.flkty = null;
        }

        /**
         * Only initialize carousel when there is
         * more than one product.
         */
        if (cells.length <= 1) {
          return;
        }

        this.flkty = new Flickity(this.container, {
          cellSelector: 'li.columns',
          contain: true,
          pageDots: false,
          prevNextButtons: true,
          wrapAround: false,
          imagesLoaded: false,
          adaptiveHeight: false,
          groupCells: true,
        });

        requestAnimationFrame(() => {
          if (!this.flkty) {
            return;
          }

          this.flkty.reloadCells();
          this.flkty.resize();
        });     
        
      }

      /**
       * Existing ScrollTrigger animation.
       */
      initAnimations() {
        if (typeof ScrollTrigger === 'undefined' || typeof gsap === 'undefined') {
          return;
        }

        ScrollTrigger.batch('.animations-true .product-card .product-featured-image-link', {
          start: 'top 90%',
          onEnter: (elements) => {
            gsap.to(elements, {
              scale: 1,
              opacity: 1,
              stagger: 0.15,
              ease: window.theme.settings.animation_easing,
            });
          },
        });
      }

      disconnectedCallback() {
        if (this.flkty) {
          this.flkty.destroy();
          this.flkty = null;
        }
      }
    }

    customElements.define('recently-viewed-recommendations', RecentlyViewedRecommendations);
  }